package org.multiplatform.kotfolio.pages.coroutines

import androidx.compose.runtime.*
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.components.WebImage
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import org.multiplatform.kotfolio.util.Constants
import org.multiplatform.kotfolio.util.Resources
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.*
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.Ul

@Page("/coroutines/flow_basic")
@Composable
fun FlowBasicPage() {
    BlogLayout(
        PageProperties(
            "Coroutines Flow",
            "Flow is a process, not a memory. No, Flows are not lifecycle-aware by default. It represents a sequence of execution. Each time you start collecting a Flow, it re-executes its process to emit values to the collectors. Flow are 2 types: Cold and Hot Flow. To safely collect flows in the Android UI layer, to avoid unnecessary flow collection when the UI goes to the background, we have to either handle the lifecycle changes manually or rely on the androidx.lifecycle:lifecycle-runtime-ktx library."
        ),
        BlogProperties("Coroutines", "Flow Basic")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            SpanText(
                text = "A Flow is generally a cold stream of data, meaning it’s defined as a sequence of values that can be collected whenever needed.",
                modifier = Modifier.fontSize(FontSize.Medium).padding(topBottom = 0.3.em)
            )

            SpanText(
                text = "A Flow is a stream of data of the same type. They are lazy (it will only start when someone requires it.), asynchronous (they are computed on coroutines) and sequential (flow goes one after the other) and there are normally 3 entities involved in the stream of data.",
                modifier = Modifier.fontSize(FontSize.Medium).padding(bottom = 0.3.em)
            )

            SpanText(
                text = "Flows are built on top of coroutines, leveraging their capabilities for asynchronous operations and structured concurrency. This means Flows can execute code asynchronously without blocking the main thread and can be easily integrated with other coroutine-based code.",
                modifier = Modifier.fontSize(FontSize.Medium).padding(bottom = 0.3.em)
            )

            SpanText(
                text = "Flows in Kotlin allow for the sequential retrieval of multiple values from coroutine-based asynchronous tasks. They are particularly useful for scenarios like receiving a stream of data over a network connection.",
                modifier = Modifier.fontSize(FontSize.Medium).padding(bottom = 0.3.em)
            )

            SpanText(
                text = "Real-World Scenario",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "we have a school cafeteria where students want to know what is being served for lunch on a school day. They would have to go to the chef’s counter to request the lunch menu and then get a response.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "We don’t want students to have to do that every day because:",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "It’s possible the menu isn’t ready yet (wasted trip).",
                                modifier = Modifier.fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "The lunch menu can change at any time, and students who already checked wouldn’t get the update.",
                                modifier = Modifier.fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "So why not just have a way to notify students who are interested when the chef puts out the menu?",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Basically, students who want lunch updates can put down their phone numbers, and when the menu is ready, the chef sends them a text message. If the menu changes later, they get another update.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Publisher / Producer / Emitter — The chef producing the lunch menu",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Data Stream — The lunch menu itself",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Subscribers / Collectors — The students who signed up for updates",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Sequential — Students receive menu updates in the order they’re sent",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Cold — A publisher(chef) only starts emitting menu after a student has signed up.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "State Management with Kotlin Flow",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            WebImage(
                src = Resources.Images.FLOW_MANAGE_COROUTINES,
                modifier = Modifier.fillMaxWidth().padding(5.px).borderRadius(10.px),
                contentDescription = "Flow State Manage"
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Producers",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li {
                        SpanText(
                            text = "Responsible for providing the data that makes up the flow (stream). A Producer produce the data that is added to stream.",
                            modifier = Modifier.padding(bottom = 0.3.em).fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Intermediaries",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li {
                        SpanText(
                            text = "(optional) that can modify the stream in some way. Operators are applied to manipulate the flow stream between the producer and consumer. Intermediator can modify the each value emitted into stream.It can also modify the stream.",
                            modifier = Modifier.padding(bottom = 0.3.em).fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Consumers",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li {
                        SpanText(
                            text = "A consumer consumes value from the stream. Collect and process the values emitted by the producer.",
                            modifier = Modifier.padding(bottom = 0.3.em)
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

            }

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Flow is a cold stream that emits values over time, allowing you to handle a continuous stream of asynchronous data.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Flow is essentially a reactive stream of data that can be emitted from a producer and collected by a consumer. In other words, Flow is a way of asynchronously emitting a sequence of values over time.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Flow do not inherently involve any concurrency. A Flow is an async sequence of values.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "With Flow, data can be emitted in a non-blocking manner, which makes it ideal for handling long-running or I/O-bound operations. Unlike LiveData, which is designed to handle UI-related events, Flow is designed to handle arbitrary data streams.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A flow is a type that can emit multiple values sequentially, as opposed to suspend functions that return only a single value. For example, you can use a flow to receive live updates from a database.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Flow produces values one at a time (instead of all at once) that can generate values from async operations like network requests, database calls, or other async code. It supports coroutines throughout its API, so you can transform a flow using coroutines as well!",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "An important characteristic of Flow is that it only starts emitting values when someone asks for them. Before someone subscribes (or performs a collection), the Flow doesn’t run its code. That’s why Flow is called as Cold Stream.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "When to Use Flow",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Stream of Data: Ideal for scenarios where data is emitted continuously, like observing database changes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Multiple Values: Flow is ideal for emitting a stream of data (like live updates).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Asynchronous: Flow is built on top of coroutines and allows for non-blocking emission and collection of data. It’s ideal for handling data that changes over time or produces multiple values, such as network requests, database changes, and user interactions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Backpressure: Flow can handle high-frequency events efficiently. t naturally manages backpressure in situations where the producer (emitter) generates data faster than the consumer (collector), preventing memory issues.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Structured Concurrency: Since it integrates with Coroutines, it provides structured concurrency benefits like cancellation and error propagation.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Pros",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Backpressure Support: Efficiently handles data streams at different rates.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Multiple Emissions: Can emit multiple values over time, unlike coroutines.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Flow work",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Flow are executed sequentially inside the same block of code (and, therefore, the same Coroutine Scope).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "low, one needs to define a producer that emits a sequence of data items, and a consumer that collects these items.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The producer is responsible for emitting data items by calling the emit() function, and the consumer is responsible for collecting these items by calling the collect() function.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A Flow in Kotlin is a type that can emit multiple values sequentially, as opposed to suspend functions that return only a single value.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "collect() function is a suspending function, which means that it can be called from a coroutine. This allows Flow to seamlessly integrate with coroutines, making it an ideal choice for asynchronous programming on Android.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Why needs flow?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Flow includes full support for coroutines. That means you can build, transform, and consume a Flow using coroutines. You can also control concurrency, which means coordinating the execution of several coroutines declaratively with Flow.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "When does a flow run",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Flow in the above example starts running when the collect operator runs.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Creating a new Flow by calling the flow builder or other APIs does not cause any work to execute.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The suspending operator collect is called a terminal operator in Flow.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "There are other suspending terminal operators such as toList, first and single shipped with kotlinx-coroutines, and you can build your own.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "By default Flow will execute",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "StateFlow and SharedFlow. We could convert a cold flow into state or shared flow in different ways.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Every time a terminal operator is applied without memory of the last run.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Until the terminal operator is canceled.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When the last value has been fully processed, and another value has been requested.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Flow introduction",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In Coroutine Flow, a Flow is a cold asynchronous stream that can emit multiple values over time. It is defined by the Flow interface, which has a single method called collect. The collect method takes a FlowCollector interface that has a single method called emit. The emit method is used to emit a value to the FlowCollector, which is then propagated downstream.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Flow interface represents the data stream that emits data items.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "FlowCollector interface represents the data receiver that consumes data items emitted by the Flow.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                KotlinCode(
                    code = """
                         fun interface Flow<T> {
                             suspend fun collect(collector: FlowCollector<T>)
                         }

                         fun interface FlowCollector<T> {
                             suspend fun emit(value: T)
                         }
                    """.trimIndent()
                )
            }

            SpanText(
                text = "Upstream and DownStream",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In the context of coroutine Flow, the term upstream refers to the source of data items that are emitted in the flow.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The downstream receiver, on the other hand, is the consumer of these data items that are collected from the upstream source.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It’s important to note that the downstream receiver can also be a flow itself, creating a chain of data processing stages where each stage collects data from the previous stage.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Flow emit value",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                SpanText(
                    text = "A Flow is a coroutine that can emit multiple values over a period of time.",
                    modifier = Modifier
                        .fontSize(FontSize.Medium)
                )

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A single coroutine / suspend function can only return one value at a time.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "You cannot write a timer that emit a value every second using a pure suspend function.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Cold flow and Hot flow",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Column {
                    SpanText(
                        text = "Cold Stream Flow, in the case of multiple collectors, the complete flow will begin from the beginning for each one of the collectors, do the task and emit the values to their corresponding collectors. It’s like 1-to-1 mapping. 1 Flow for 1 Collector. It means a cold flow can’t have multiple collectors as it will create a new flow for each of the collectors.",
                        modifier = Modifier.padding(topBottom = 0.3.em)
                            .fontSize(FontSize.Medium)
                    )

                    SpanText(
                        text = "Hot Stream Flow, in the case of multiple collectors, the flow will keep on emitting the values, collectors get the values from where they have started collecting. It’s like 1-to-N mapping. 1 Flow for N Collectors. It means a hot flow can have multiple collectors.",
                        modifier = Modifier.padding(bottom = 0.3.em)
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Cold flow",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Sequence, Flow",
                            modifier = Modifier.padding(bottom = 0.3.em)
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Flow is cold by default.",
                            modifier = Modifier.padding(bottom = 0.3.em)
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Start on demand: A cold stream begins emitting values only when a subscriber actively subscribes to it. The data source are lazy.",
                            modifier = Modifier.padding(bottom = 0.3.em)
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Independent emissions: Each subscriber receives its own independent sequence of values. No elements are stored.",
                            modifier = Modifier.padding(bottom = 0.3.em)
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "A “cold flow” is ideal when a flow requires a single collector. It remains inactive until a collector is present (No data is produced until a collector subscribes). Each collector receives a new stream. Each collector operates with its own instance of the underlying cold flow. Once collected, it concludes its operation and ceases to exist.",
                            modifier = Modifier.padding(bottom = 0.3.em)
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "It can only be executed on collection. It does not start producing values until one starts to collect them.",
                            modifier = Modifier.padding(bottom = 0.3.em)
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "A cold stream is a flow that triggers the same code every time it is collected.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "It can be built using the flow builder.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Cold flows are created on-demand and emit data when they’re being observed.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "A cold flow is a type of flow that executes the producer block of code on-demand when a new subscriber collects.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Stops emission when any collector is not active — Cold flows are lazy.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "In general think of it like a stream of data flowing in a pipe with both ends having a producer and consumer running on a coroutine.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "It’s like a Water Tap that only releases water when someone turns it on.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "A cold stream does not start producing values until one starts to collect them.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Think of it as a calculated or generated stream of data that each collector can start from scratch. A Flow does not require a separate coroutine actively sending items in real time — it can produce them on demand in a suspending sequence. Multiple collectors each get to see the same series of emissions, but they each start the Flow from the beginning independently.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = " Think of it like a recorded video. Each viewer watches from the beginning at their own pace.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Hot flow",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "StateFlow, channel, Collections (List, Set …)",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Start immediately: begins emitting values regardless of whether there are subscribers.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Store the elements: they don’t need to be recalculated and all subscribers receive the same sequence of values.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Hot Flows start emitting values as soon as they are activated and do not restart for new collectors. Both StateFlow and SharedFlow are examples of Hot Flows but are designed for different use cases. Hot flows are always in memory even there’s no collectors. (Hot flow can store the data.)",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "It persists in memory until it’s garbage collected and emits events from potentially multiple coroutines. Interested parties can subscribe by invoking collect, and all collectors will receive the same event emitted from that hot flow, similar to an event bus.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "They are active by default.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Hot flows are always active and can emit data regardless of whether or not they’re being observed. (Hot flow emits data even when there is no collector.)",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Hot flows are better for sharing a state across multiple observers.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "A hot flow is a stream whose active instance exists independently of the presence of collectors.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "It starts producing values immediately.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "SharedFlow emits all value to all consumers in a broadcast fashion.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "StateFlow is SharedFlow, but only emits the latest value to its listeners and requires an initial value. It works like LiveData",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "A hot stream on the other hand starts producing values immediately either any one is collecting it or not.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "A SharedFlow “broadcasts” data to all active collectors in real time — like an event bus. Everyone who’s collecting at the time of emission sees it, and you can configure how many items to replay to latecomers. This still differs from a Channel, since each emission is not consumed uniquely by a single receiver; instead, all collectors receive it. That makes it great for distributing UI state changes or events across different parts of your app.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            //https://medium.com/@YodgorbekKomilo/understanding-cold-and-hot-flows-in-jetpack-compose-for-android-e0430ccfc169
            WebImage(
                src = Resources.Images.FLOW_COLD_VS_HOT,
                modifier = Modifier.fillMaxWidth().padding(5.px).borderRadius(10.px),
                contentDescription = "Cold vs Hot Flow"
            )

            SpanText(
                text = "Use Cases for Cold Flows",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Perform work only when data is needed.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Ensure each collector receives an independent sequence of data.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Implement isolated processing for each subscriber.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Use Cases for Hot Flows",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "You need to share data across multiple subscribers.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The producer should stay active regardless of active collectors.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State retention is required for new collectors to receive the latest value.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Swift Combine Framework",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Publisher (Observables): Something that gives (emits) values over time. Like a waterr pipe giving water drops.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Subscriber (Observers): Something that receives values from a publisher. Like a bucket catching water.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Operators: Modifiers that change or filter data. Like filters on the pipe.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Subjects: Can both send and receive values Like a walkie-talkie.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Cancellable: A token that you can use to stop receiving updates. Like turning off the pipe.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Are Flows lifecycle-aware?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "No, Flows are not lifecycle-aware by default. However, you can make them lifecycle-aware through the following ways.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "repeatOnLifecycle: Use this to collect multiple flows within a lifecycle-aware scope.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "flowWithLifecycle: Internally uses repeatOnLifecycle and is ideal for collecting a single flow.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Manual Lifecycle Checks: Verify lifecycle state before updating the UI.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "LifecycleScope (Lifecycle-aware manner)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "lifecycleScope is tied to a Lifecycle owner — an Activity or Fragment. It's cancelled when that lifecycle is destroyed.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Flows are not lifecycle-aware by default, meaning they can continue emitting data even when a Fragment or Activity goes to the background, which can lead to memory leaks or unnecessary resource consumption. To collect Flow in a lifecycle-aware manner, extension functions provided by Jetpack libraries are used.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "To safely collect flows in the Android UI layer, to avoid unnecessary flow collection when the UI goes to the background, we have to either handle the lifecycle changes manually or rely on the androidx.lifecycle:lifecycle-runtime-ktx library.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It is recommended to call Lifecycle.repeatOnLifecycle on the activity’s onCreate or the fragment’s onViewCreated function.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "launchWhenCreated{} starts when lifecycle is at least in Lifecycle.State.CREATED state",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "launchWhenStarted{} starts when lifecycle is at least in Lifecycle.State.STARTED state",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "launchWhenResumed{} starts when lifecycle is at least in Lifecycle.State.RESUMED state",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "repeatOnLifecycle{} this is the most recommended and safe method for collecting Flow in a lifecycle-aware manner. It executes a code block when a specific lifecycle state is reached and cancels the block when the lifecycle falls below that state. This ensures resources are only used when needed.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            KotlinCode(
                code = """
                    override fun onCreate(savedInstanceState: Bundle?) {
                       lifecycleScope.launch {
                           repeatOnLifecycle(Lifecycle.State.STARTED) {
                               // will stop when onStop() is called
                               // use separate launch for each collect
                               launch {
                                   viewModel.someFunction().collect { ... }
                               }
                               launch {
                                   viewModel.anotherFunction().collect { ... }
                               }
                           }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Flow.flowWithLifecycle",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "If you only need to perform lifecycle-aware collection on a single flow, you can use the Flow.flowWithLifecycle() method to simplify your code:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    lifecycleScope.launch {
                        viewModel.someFunction()
                            .flowWithLifecycle(lifecycle, Lifecycle.State.STARTED)
                            .collect { ... }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    fun <T> rememberFlowWithLifecycle(
                        flow: Flow<T>,
                        lifecycle: Lifecycle = LocalLifecycleOwner.current.lifecycle,
                        minActiveState: Lifecycle.State = Lifecycle.State.STARTED
                    ): Flow<T> = remember(flow, lifecycle) {
                        flow.flowWithLifecycle(
                            lifecycle = lifecycle,
                            minActiveState = minActiveState
                        )
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "map Vs. transform",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "One-to-one Transformation: Use map when you need to transform each value emitted by the Flow into a single new value. Go for map when you have straightforward value conversions. Example: Converting a Flow of integers to a Flow of their squared values.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Flexible Emission: Use transform when you need more control over the emission of values. You can emit multiple values, skip values, or even change the type of the Flow. Opt for transform when you need to implement custom logic or control the number and type of emitted values. Example: Emitting only even numbers from a Flow of integers.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "map Vs. mapLatest",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "With map() the entire Flow will be executed in sequence within a single coroutine, meaning delay() will only start running after the loading operation completes. As a result, each loading operation will be delayed by the amount of time it took to perform the previous loading operation, plus the fixed interval.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "With mapLatest() the main coroutine will collect the upstream values of tickerFlow() while a child coroutine will be created to perform the loading operation concurrently and collect the result without suspending the main coroutine. This means that delay() will start running immediately after the previous tick and each loading operation will start precisely according to schedule. This also means that the interval must be longer than the typical loading time because it will act as a timeout: a previous loading operation that did not complete in time when tickerFlow() emits a new value will be canceled. A new child coroutine then replaces the previous one to execute the next loading operation.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "why use Flow instead of ChannelFlow by default?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It’s faster on high-speed emissions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "You do not overthink the behavior of your data streams.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Its behavior and lifecycle are the same as any other default implementation in Kotlin. If you see flow {}, you know that all of them behave the same. ChannelFlow, at the same time, is a signal to you that there could be complex logic or a structure of coroutines which produce a single stream of data.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Potentially harder to test.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Flows Vs. Channels",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Flows are a good fit for sequential data processing while Channel are good fit for concurrent data flow processing.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Flow will not send any data until any receiver is not available to receive it while it is not the case for channel. Channel can emit data no matter any receiver is available or not.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Channels aren’t scoped to a specific lifecycle, unlike flows. If we want to close/cancel a Channel, we need to explicitly use the methods provided by the Channel API. So, let’s say we need to have a data stream that survives across the screen lifecycle, then we have to use channels while On the other hand, if we need to scope the emissions to a certain component’s lifecycle, flows are handy. For instance, if we’re using the MVVM architecture and viewModelScope, all the flows started with that scope will be canceled when ViewModel gets cleared.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Channel stream is like a buffer, which has the buffer capacity. The default buffer capacity is zero. By default, when the buffer is overflow, the data sending is suspended until there is a receiver.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Whenever Flow<T>collect() is called, a new cold stream is created and data collected into the new stream while for channel receiver does not create a new stream, It still uses the existing stream.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "ChannelFlow Vs SharedFlow",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "ChannelFlow- Stream of data sending over a channel.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "SharedFlow-stream of data sent over a flow.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "With ChannelFlow, when buffer is full, Since we are communicating with channel producer will get blocked.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "With SharedFlow, when buffer is full, there is no blocking mechanism here it will still produce the value and overridden the oldest value in the buffer which is called conflated behaviour.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "And reg the slow collectors case, in ChannelFlow both slow collectors will get the values without any drop but for SharedFlow you can get some values being droped because of Conflated behaviour.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Flow vs SharedFlow",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Flow are cold data source but shared flow is made as an exception as this is a hot data source.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Flow cache replay values depend on the value passed in constructor. It can handle multiple listeners.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Not using repeatOnLifecycle for flow collection",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Collecting a flow in lifecycleScope.launch without repeatOnLifecycle means the collection continues when the app is backgrounded. Use repeatOnLifecycle(Lifecycle.State.STARTED) to automatically pause and resume collection with the lifecycle.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Creating a new scope manually when a lifecycle-aware one exists",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "CoroutineScope(Job()) created manually in a ViewModel has no automatic cancellation. Use viewModelScope instead.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "What happens to a Flow if the Coroutine collecting it is cancelled? Does it leak memory?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "No memory leaks! Flow is perfectly tied to Structured Concurrency. When the collecting Coroutine is cancelled (for example, when a ViewModel is cleared), the collector stops listening. Because the Flow is cold, this cancellation propagates upstream, and the producer (flow { ... } block) is immediately cancelled and stops emitting.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "How does Flow ensure Context Preservation, and why will the compiler yell at you if you use withContext inside a Flow builder?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Flow guarantees Context Preservation, meaning the flow builder must emit in the same CoroutineContext as the collector. If you try to use withContext(Dispatchers.IO) inside the flow {} builder, it violates this rule and throws an IllegalStateException. To change the dispatcher for the upstream (the producer), you must use the .flowOn(Dispatchers.IO) operator. This architectural choice prevents downstream collectors (like the UI) from accidentally executing on a background thread.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }
        }
    }
}
//https://medium.com/@anitaa_1990/kotlin-flows-fundamentals-7d9b984f12bb
//https://medium.com/@sandeepkella23/part-1-kotlin-flows-the-air-compressor-of-android-fundamentals-cold-streams-7ae94471c1d7